import React from 'react'
import {
  createSearchParams,
  matchRoutes,
  resolvePath,
  useNavigate as useReactRouterNavigate,
  useSearchParams as useReactRouterSearchParams,
  type NavigateOptions,
  type SetURLSearchParams,
  type To,
  useLocation,
} from 'react-router-dom'

import isHashNavigation from '@github-ui/is-hash-navigation'
import {AppContext} from '@github-ui/react-core/app-context'
import {startSoftNav} from '@github-ui/soft-nav/state'

export const useNavigate = (): ((to: To, options?: NavigateOptions) => void) => {
  const {routes, history} = React.useContext(AppContext)
  const reactRouterNavigate = useReactRouterNavigate()
  return React.useCallback(
    (to, options) => {
      const pathname = resolvePath(to).pathname
      const isExternalToApp = !matchRoutes(routes, pathname)

      if (isExternalToApp) {
        const href = history.createHref(to)
        ;(async () => {
          const {softNavigate: turboSoftNavigate} = await import('@github-ui/soft-navigate')
          turboSoftNavigate(href)
        })()
      } else {
        if (!isHashNavigation(location.href, to.toString())) {
          startSoftNav('react')
        }
        reactRouterNavigate(to, options)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {turbo, ...state} = window.history.state
        window.history.replaceState({...state, skipTurbo: true}, '', location.href)
      }
    },
    [history, reactRouterNavigate, routes],
  )
}

/**
 * An implementation of `useSearchParams` that mirrors `react-router-dom`'s `useSearchParams` hook
 * but utilizes `@github-ui/useNavigate` instead of `react-router` `useNavigate` to handle updates.
 */
export const useSearchParams = () => {
  const [searchParams] = useReactRouterSearchParams()
  const navigate = useNavigate()
  const {pathname} = useLocation()

  const setSearchParams = React.useCallback<SetURLSearchParams>(
    (nextInit, navigateOptions) => {
      const newSearchParams = createSearchParams(typeof nextInit === 'function' ? nextInit(searchParams) : nextInit)
      navigate(
        {
          pathname,
          search: newSearchParams.toString(),
        },
        navigateOptions,
      )
    },
    [searchParams, navigate, pathname],
  )

  return [searchParams, setSearchParams] as const
}
